'use client';

import { MaterialReactTable, type MRT_VisibilityState } from 'material-react-table';
import { Download } from '@mui/icons-material';
import { Avatar, Button, IconButton, ListItemIcon, Menu, MenuItem, TextField, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import * as Yup from 'yup';
import TitleBar from '../layout/TitleBar';
import { US_STATES, yupPasswordValidation } from '@/constants/form.constants';
import { useCreateUser, useQueryUsers, useUpdateUser, fetchUserDetailsForAdmin } from '@/hooks/api/useUsers';
import { useBusinessUnitsAll } from '@/hooks/api/useBusinessUnits';
import { useNotification } from '@/contexts/NotificationContext';
import GenericFormModal from '../form/GenericFormModal';
import { GENERIC_FORM_FIELD_TYPE } from '../form/GenericForm';
import { useNavigate } from 'react-router-dom';
import GenericActionPicker, { GENERIC_ICON_NAME } from '../form/GenericActionPicker';
import UserGpsRouteModal from './UserGpsRouteModal';

/** Coerce API / MUI select values to numeric ids so updates are not sent as []. */
function normalizeBusinessUnitIds(raw: unknown): number[] {
  if (raw == null) return [];
  if (Array.isArray(raw)) {
    return raw.map((x) => Number(x)).filter((n) => Number.isFinite(n));
  }
  const n = Number(raw);
  return Number.isFinite(n) ? [n] : [];
}

const UsersPage: React.FC = () => {
  const [data, setData] = useState([] as any[]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [promoteToAdminVisible, setPromoteToAdminVisible] = useState(false);
  const [demoteFromAdminVisible, setDemoteFromAdminVisible] = useState(false);
  const [loginAsUserConfirmVisible, setLoginAsUserConfirmVisible] = useState(false);
  const [gpsRouteModalVisible, setGpsRouteModalVisible] = useState(false);

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [selectedUserForGps, setSelectedUserForGps] = useState<any>(null);
  const [sorting, setSorting] = useState([] as any[]);
  const [search, setSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);
  const navigate = useNavigate()

  const queryUsersMutation = useQueryUsers();
  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const { data: businessUnitOptions = [] } = useBusinessUnitsAll(true);
  const { showSuccess, showError } = useNotification();

  // Column visibility state with localStorage persistence
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(() => {
    try {
      const storedVisibility = localStorage.getItem('usersPage_columnVisibility');
      return storedVisibility ? JSON.parse(storedVisibility) : { 
        id: false, 
        active: false, 
        createdAt: false, 
        updatedAt: false,
        lastLogin: false,
        loginFailCount: false,
        emailConfirmed: false,
        phoneConfirmedDate: false,
        addressLine1: false,
        city: false,
        subdivision: false,
        addressLine2: false,
        postalCode: false,
        locality: false,
        country: false,
        lat: false,
        long: false
      };
    } catch (error) {
      console.error("Failed to parse column visibility from localStorage", error);
      return { 
        id: false, 
        active: false, 
        createdAt: false, 
        updatedAt: false,
        lastLogin: false,
        loginFailCount: false,
        emailConfirmed: false,
        phoneConfirmedDate: false,
        addressLine1: false,
        city: false,
        subdivision: false,
        addressLine2: false,
        postalCode: false,
        locality: false,
        country: false,
        lat: false,
        long: false
      };
    }
  });

  useEffect(() => {
    localStorage.setItem('usersPage_columnVisibility', JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  const columns = useMemo(() => [
    {
      accessorKey: 'id',
      header: 'ID',
      enableHiding: true,
    },
    {
      id: 'avatar',
      header: 'Avatar',
      Cell: ({ row }: any) => {
        const user = row.original;
        const initials = user.initials || (user.firstName?.[0] || '') + (user.lastName?.[0] || '') || 'U';
        return (
          <Avatar
            src={user.profileImageUrl || undefined}
            sx={{ width: 32, height: 32 }}
          >
            {!user.profileImageUrl && initials}
          </Avatar>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'roles',
      header: 'Roles',
    },
    {
      accessorKey: 'businessUnits',
      header: 'Business units',
      Cell: ({ row }: any) => {
        const v = row.original.businessUnits;
        return v && String(v).trim() ? v : '—';
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      Cell: ({ row }: any) => {
        return <GenericActionPicker row={row.original} actions={[
          {
              icon: GENERIC_ICON_NAME.EDIT,
              label: "Edit",
              onClick: async (row) => {
                const rowData = row;
                try {
                  const d = await fetchUserDetailsForAdmin(rowData.id);
                  const a = d.address || {};
                  setSelectedUser({
                    ...rowData,
                    ...d.user,
                    ...a,
                    businessUnitIds: normalizeBusinessUnitIds(d.businessUnitIds ?? []),
                  });
                  setEditModalVisible(true);
                } catch {
                  showError('Failed to load user details');
                }
              }
          },
          {
            icon: GENERIC_ICON_NAME.VIEW,
              label: "View Details",
              onClick: (row) => {
                const rowData = row;
                setSelectedUser(rowData)
                navigate('/app/users/' + row?.id)
              }
          },
          {
            icon: GENERIC_ICON_NAME.MAP,
            label: "View GPS Route",
            onClick: (row) => {
              const rowData = row;
              console.log('🔵 [UsersPage] Setting selectedUserForGps:', rowData);
              setSelectedUserForGps(rowData);
              setGpsRouteModalVisible(true);
            }
          }
      ]} />
      },
    },
    {
      accessorKey: 'phone',
      header: 'Phone',
    },
    {
      accessorKey: 'firstName',
      header: 'First Name',
    },
    {
      accessorKey: 'lastName',
      header: 'Last Name',
    },
    {
      accessorKey: 'addressLine1',
      header: 'Address',
    },
    {
      accessorKey: 'city',
      header: 'City',
    },
    {
      accessorKey: 'subdivision',
      header: 'State',
    },
    {
      accessorKey: 'active',
      header: 'Active',
      Cell: ({ row }: any) => row.original.active !== false ? 'Yes' : 'No',
      enableHiding: true,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created At',
      Cell: ({ row }: any) => row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
    {
      accessorKey: 'updatedAt',
      header: 'Updated At',
      Cell: ({ row }: any) => row.original.updatedAt ? new Date(row.original.updatedAt).toLocaleDateString() : '-',
      enableHiding: true,
    },
    {
      accessorKey: 'lastLogin',
      header: 'Last Login',
      Cell: ({ row }: any) => row.original.lastLogin ? new Date(row.original.lastLogin).toLocaleDateString() : '-',
      enableHiding: true,
    },
    {
      accessorKey: 'loginFailCount',
      header: 'Login Fail Count',
      enableHiding: true,
    },
    {
      accessorKey: 'emailConfirmed',
      header: 'Email Confirmed',
      Cell: ({ row }: any) => row.original.emailConfirmed ? 'Yes' : 'No',
      enableHiding: true,
    },
    {
      accessorKey: 'phoneConfirmedDate',
      header: 'Phone Confirmed',
      Cell: ({ row }: any) => row.original.phoneConfirmedDate ? new Date(row.original.phoneConfirmedDate).toLocaleDateString() : '-',
      enableHiding: true,
    },
    {
      accessorKey: 'addressLine2',
      header: 'Address Line 2',
      enableHiding: true,
    },
    {
      accessorKey: 'postalCode',
      header: 'Postal Code',
      enableHiding: true,
    },
    {
      accessorKey: 'locality',
      header: 'Locality',
      enableHiding: true,
    },
    {
      accessorKey: 'country',
      header: 'Country',
      enableHiding: true,
    },
    {
      accessorKey: 'lat',
      header: 'Latitude',
      enableHiding: true,
    },
    {
      accessorKey: 'long',
      header: 'Longitude',
      enableHiding: true,
    },
  ], []);

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Users' }, // No href for the current page
  ];

  const rightContent = (
    <>
      <Button
        variant="outlined"
        startIcon={<Download />}
        sx={{ textTransform: 'none' }}
      >
        Export
      </Button>
      <Button onClick={() => {
        setCreateModalVisible(true)
      }} variant="contained" sx={{ textTransform: 'none' }}>
        + Add User
      </Button>
    </>
  );

  const sharedUserFields = [{
    groupName: 'main',
    fields: [
      { label: 'First Name', name: 'firstName', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Last Name', name: 'lastName', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Phone', name: 'phone', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 6 },
      { label: 'Address', name: 'addressLine1', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 12 },
      {
        label: 'State',
        name: 'subdivision',
        type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT,
        options: US_STATES,
        required: false,
        columns: 6
      },
      { label: 'City', name: 'city', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: false, columns: 6 },
    ]
  }]


  const rolesField = {
    label: 'Role', name: 'roles', type: GENERIC_FORM_FIELD_TYPE.SINGLE_SELECT, required: false, columns: 12 ,
    options: [
      {value: 'Admin', label: 'Admin'},
      {value: 'SystemAdmin', label: 'System Admin'},
      {value: 'Technician', label: 'Technician'},
      {value: 'Accounting', label: 'Accounting'},
    ]
  }

  const businessUnitsField = {
    label: 'Business units',
    name: 'businessUnitIds',
    type: GENERIC_FORM_FIELD_TYPE.MULTI_SELECT,
    required: false,
    columns: 12,
    options: businessUnitOptions.map((b) => ({ value: b.id, label: b.name })),
  };

  const emailField = { label: 'Email', name: 'email', type: GENERIC_FORM_FIELD_TYPE.TEXT, required: true, columns: 12 }

  const addEditFields = [
    { label: 'ID', name: 'id', type: GENERIC_FORM_FIELD_TYPE.HIDDEN, required: true, columns: 12 },
    {
      label: '',
      name: 'changePassword',
      type: GENERIC_FORM_FIELD_TYPE.CHECKBOX,
      required: false,
      columns: 12,
      inlineNode: <div>Change password?</div>
    },
    {
      label: 'Password',
      name: 'password',
      type: GENERIC_FORM_FIELD_TYPE.PASSWORD,
      validation: Yup.string()
        .when('changePassword', {
          is: true, // Only validate if changePassword is true
          then: (schema) =>
            schema
              .required('Password is required')
              .test('password-validation', 'Password must meet requirements', function (value) {
                if (value) {
                  return yupPasswordValidation.isValidSync(value); // Validate only if value is present
                }
                return true; // If no value, pass validation
              }),
          otherwise: (schema) => schema.notRequired(), // Skip validation if changePassword is false
        }),
      required: false,
      showConditions: [{ key: 'changePassword', value: true }],
      columns: 6,
    },
    {
      label: 'Confirm Password',
      name: 'confirmPass',
      type: GENERIC_FORM_FIELD_TYPE.PASSWORD,
      validation: Yup.string()
        .when(['changePassword', 'password'], {
          is: (changePassword: any, password: any) => changePassword && !!password, // Only validate if changePassword is true and password is present
          then: (schema) =>
            schema
              .oneOf([Yup.ref('password')], 'Passwords must match')
              .required('Confirm Password is required'),
          otherwise: (schema) => schema.notRequired(), // Skip validation if conditions are not met
        }),
      required: false,
      showConditions: [{ key: 'changePassword', value: true }],
      columns: 6,
    },
  ];
  
  const editUserFields = cloneDeep(sharedUserFields);
  editUserFields[0].fields = editUserFields[0].fields.concat(addEditFields);
  editUserFields[0].fields.push(rolesField);
  editUserFields[0].fields.push(businessUnitsField);

  const createUserFields = cloneDeep(sharedUserFields);
  createUserFields[0].fields.push(emailField);
  createUserFields[0].fields.push(rolesField);
  createUserFields[0].fields.push(businessUnitsField);

  const demoteFromAdminFields = [{
    groupName: 'main',
    fields: [
      { label: '', name: 'id', type: GENERIC_FORM_FIELD_TYPE.HIDDEN, required: true, columns: 12 },
      { label: '', name: 'confirm', type: GENERIC_FORM_FIELD_TYPE.CHECKBOX, required: true, columns: 12,
        inlineNode: <p>Are you sure you want to demote {selectedUser?.email} from an Administrator?</p> 
       },
    ]
  }]

  const promoteToAdminFields = [{
    groupName: 'main',
    fields: [
      { label: '', name: 'id', type: GENERIC_FORM_FIELD_TYPE.HIDDEN, required: true, columns: 12 },
      { label: '', name: 'confirm', type: GENERIC_FORM_FIELD_TYPE.CHECKBOX, required: true, columns: 12,
        inlineNode: <p>Are you sure you want to promote {selectedUser?.email} to an Administrator?</p>
       },
    ]
  }]

  const queryUsers = queryUsersMutation.mutateAsync;
  const orderBy = sorting[0]?.id || 'id';
  const orderDirection: 'ASC' | 'DESC' = sorting[0]?.desc ? 'DESC' : 'ASC';

  const handlePaginationChange = useCallback((updater: any) => {
    setPagination((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (next.pageIndex === prev.pageIndex && next.pageSize === prev.pageSize) {
        return prev;
      }
      return next;
    });
  }, []);

  const handleSortingChange = useCallback((updater: any) => {
    setSorting((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (
        prev.length === next.length &&
        prev.every((s: any, i: number) => s.id === next[i]?.id && s.desc === next[i]?.desc)
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const result = await queryUsers({
          skip: pagination.pageIndex * pagination.pageSize,
          take: pagination.pageSize,
          search,
          orderBy,
          orderDirection,
        });
        if (!cancelled) {
          setData(result.data || []);
          setTotalRows(result.total || 0);
        }
      } catch (error: any) {
        if (!cancelled) {
          showError(error.message || 'Failed to fetch users');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
  }, [pagination.pageIndex, pagination.pageSize, orderBy, orderDirection, search, tableRefreshKey, queryUsers, showError]);

  console.log("selectedUser?", selectedUser)

  const extractRoleOptionsFromString = (roles: string) => {
    if (!roles){
      return ''
    }
    // Return the first role (single select)
    const roleArray = roles.split(",").map((role: string) => {
      return role.trim()
    })
    return roleArray[0] || ''
  }

  return (
    <>
      {/* <Loading loading={isLoading} /> */}

      <GenericFormModal 
      visible={loginAsUserConfirmVisible} 
      onDismiss={() => {
        setLoginAsUserConfirmVisible(false)
      }}
        onSubmit={async (values: any) => {
        //   setIsLoading(true);
        //   const response = await fetchWithAuth('/api/auth/login-as-user', {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify({
        //       id: selectedUser.id
        //     }),
        //   });
        //   const result = await response.json();
        //   // Store the JWT in localStorage
        //   localStorage.setItem('jwt', result.token);
        //   setCookie(null, 'jwt', result.token, {
        //     maxAge: 30 * 24 * 60 * 60, // 30 days
        //     path: '/',
        //     secure: true,
        //     httpOnly: false, // Change to true if setting server-side
        //   });
        //   window.dispatchEvent(new Event('jwt-changed')); // Notify changes

        //   if (selectedUser.isAdmin){
        //     router.push(ROUTES.ADMIN_DASHBOARD);
        //   }
        //   else{
        //     // Redirect to the /dashboard route
        //     router.push(ROUTES.ACCOUNT_DASHBOARD)
        //   }
        //   // Redirect to the /admin/dashboard route
 
        //   setIsLoading(false);
        }}
        initialValues={{
          id: selectedUser?.id
        }}
        fieldGroups={[]}
        title={'Login as User'} 
        description={`Are you sure you want to login as ${selectedUser?.email}?`}
        />


      <GenericFormModal visible={promoteToAdminVisible} onDismiss={() => {
        setPromoteToAdminVisible(false)
      }}
        onSubmit={async (values: any) => {
        //   setIsLoading(true);
        //   console.log("values?", values)
        //   await toggleAdmin.mutateAsync({
        //     isAdmin: true,
        //     ...values
        //   });
        //   setPromoteToAdminVisible(false)
        //   setTableRefreshKey(tableRefreshKey + 1)
        //   setIsLoading(false);
        }}
        initialValues={{
          id: selectedUser?.id,
          confirm: false,
        }}
        fieldGroups={promoteToAdminFields}
        title={'Promote to admin'} />

<GenericFormModal visible={demoteFromAdminVisible} onDismiss={() => {
        setDemoteFromAdminVisible(false)
      }}
        onSubmit={async (values: any) => {
        //   setIsLoading(true);
        //   console.log("values?", values)
        //   await toggleAdmin.mutateAsync({
        //     isAdmin: false,
        //     ...values
        //   });
        //   setDemoteFromAdminVisible(false)
        //   setTableRefreshKey(tableRefreshKey + 1)
        //   setIsLoading(false);
        }}
        initialValues={{
          id: selectedUser?.id,
          confirm: false,
        }}
        fieldGroups={demoteFromAdminFields}
        title={'Demote from admin'} />


      <GenericFormModal visible={createModalVisible} onDismiss={() => {
        setCreateModalVisible(false)
      }}
        onSubmit={async (values: any) => {
          try {
            await createUserMutation.mutateAsync({
              ...values,
              roles: values.roles || '',
              businessUnitIds: normalizeBusinessUnitIds(values.businessUnitIds),
            });
            setCreateModalVisible(false);
            setTableRefreshKey((key) => key + 1);
            showSuccess('User created successfully');
          } catch (error: any) {
            showError(error.message || 'Failed to create user');
          }
        }}
        initialValues={{ businessUnitIds: [] }}
        fieldGroups={createUserFields}
        title={'Create User'} />

      <GenericFormModal
        visible={editModalVisible}
        onDismiss={() => {
        setEditModalVisible(false)
      }}
        formKey={selectedUser?.id}
        enableReinitialize
        onSubmit={async (values: any) => {
          console.log("submitting here", values)
          console.log("roles value:", values.roles, "type:", typeof values.roles)
          // Ensure roles is sent as a string (single select returns string)
          const payload = {
            ...values,
            roles: values.roles || '',
            businessUnitIds: normalizeBusinessUnitIds(values.businessUnitIds),
          };
          console.log("payload with roles:", payload)
          updateUserMutation.mutate(payload, {
            onSuccess: () => {
              setEditModalVisible(false)
              setTableRefreshKey((key) => key + 1)
              showSuccess("User updated successfully")
            },
            onError: (error: any) => {
              showError(error.message || 'Failed to update user')
            },
          })
        //   setIsLoading(true);
        //   await updateUser.mutateAsync(values);
        //   setEditModalVisible(false)
        //   setTableRefreshKey(tableRefreshKey + 1)
        //   setIsLoading(false);
        }}
        initialValues={{
          id: selectedUser?.id,
          firstName: selectedUser?.firstName,
          lastName: selectedUser?.lastName,
          phone: selectedUser?.phone,
          addressLine1: selectedUser?.addressLine1,
          city: selectedUser?.city,
          subdivision: selectedUser?.subdivision,
          roles: extractRoleOptionsFromString(selectedUser?.roles),
          businessUnitIds: normalizeBusinessUnitIds(selectedUser?.businessUnitIds ?? []),
        }}
        fieldGroups={editUserFields}
        title={'Edit User'} />

      <UserGpsRouteModal
        open={gpsRouteModalVisible}
        onClose={() => {
          setGpsRouteModalVisible(false);
          setSelectedUserForGps(null);
        }}
        userId={selectedUserForGps?.id}
        userName={selectedUserForGps ? `${selectedUserForGps.firstName || ''} ${selectedUserForGps.lastName || ''}`.trim() : undefined}
      />
      {gpsRouteModalVisible && console.log('🔵 [UsersPage] Modal open, selectedUserForGps:', selectedUserForGps, 'userId:', selectedUserForGps?.id)}

      <TitleBar
        breadcrumbs={breadcrumbs}
        title="Users"
        rightContent={rightContent}
      />
      <MaterialReactTable
        columns={columns}
        data={data}
        manualPagination
        manualSorting
        enableGlobalFilter={false} // Disable built-in global filter since we're handling search externally
        onPaginationChange={handlePaginationChange}
        onSortingChange={handleSortingChange}
        onColumnVisibilityChange={setColumnVisibility}
        rowCount={totalRows}
        initialState={{ 
          density: 'compact',
          columnVisibility 
        }}
        state={{
          isLoading,
          pagination,
          sorting,
          columnVisibility,
        }}
        renderTopToolbarCustomActions={() => (
          <TextField
            placeholder="Search"
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{
              width: '300px',
              '& .MuiOutlinedInput-root': {
                borderRadius: '24px',
                backgroundColor: 'background.paper',
                color: 'text.primary',
              },
              '& .MuiOutlinedInput-input::placeholder': {
                color: 'text.secondary',
                opacity: 1,
              },
            }}
          />
        )}
      />
    </>
  );
};

export default UsersPage;
